import { useNavigate, useParams } from "react-router-dom";
import { BookingFlowHeader } from "../booking-flow-header";
import { PackageDetails } from "../package-details";
import { CycleDetails } from "../cycle-details";
import { Controller, useForm } from "react-hook-form";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { FileX2, AlertCircle } from "lucide-react";
import { DownsellCyclesTable } from "../updated-cycles-table";
import { AffectedBookingsTable } from "../affected-bookings-table";
import { Button } from "@/components/ui/button";
import {
  BookingDownsellFormData,
  DownsellType,
} from "./booking-downsell-page.types";
import { yupResolver } from "@hookform/resolvers/yup";
import { BookingDownsellSchema } from "./booking-downsell-page.validation";
import { useDownsellFlow } from "../../context/use-downsell-flow";
import { useEffect, useState } from "react";
import { GetBookingDownsellApi } from "../../adapters/get-booking-downsell";
import { BOOKING_STATUS } from "@/constants/booking-status";

export const BookingDownsellPage = () => {
  const navigate = useNavigate();
  const { bookingId } = useParams();

  const {
    handleSubmitDownsellData,
    handleMapDownsellData,
    downsellDetailsData,
    handleBookingDownsellSimulation,
    simulatedDownsell,
  } = useDownsellFlow();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchBookingData = async () => {
      try {
        if (!bookingId) return navigate("/private-office/bookings");
        const data = await GetBookingDownsellApi({ id: Number(bookingId) });

        if (!data) return navigate("/private-office/bookings");

        if (
          [BOOKING_STATUS.CANCELLED, BOOKING_STATUS.USER_CANCELLED].includes(
            data.booking.status,
          )
        ) {
          return navigate("/private-office/bookings");
        }

        await handleMapDownsellData(data);
      } catch (error) {
        console.error("Error fetching booking data:", error);
        alert("Erro ao buscar dados do booking. Por favor, tente novamente.");
        return navigate("/private-office/bookings");
      } finally {
        setIsLoading(false);
      }
    };

    fetchBookingData();
  }, [bookingId]);

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<BookingDownsellFormData>({
    resolver: yupResolver(BookingDownsellSchema),
    mode: "onBlur",
    reValidateMode: "onChange",
  });

  const onSubmit = (data: BookingDownsellFormData) => {
    handleSubmitDownsellData(data);
  };

  if (isLoading || !downsellDetailsData) return <></>;

  const isPackageMonthly =
    downsellDetailsData?.packageDetails.duration === "monthly";
  const hasPaidOrIsInvoicedInstallment =
    (simulatedDownsell?.updatedCycles || []).some(
      (cycle) => cycle.hasPaidOrInvoicedInstallments,
    ) || false;
  const hasInstallmentWithEnoughCredits =
    (simulatedDownsell?.updatedCycles || []).some(
      (cycle) => cycle.hasInstallmentWithEnoughCredits,
    ) || false;

  return (
    <div className="bg-slate-50">
      <div className="p-6 max-w-5xl mx-auto">
        <BookingFlowHeader
          title="Cancelando reserva de escritório privativo"
          subtitle="Revise os detalhes da reserva abaixo e escolha como proceder com o cancelamento."
          onClick={() => navigate("/private-office/bookings")}
        />
        <form className="w-full" onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-8 mt-6">
            <div className="border rounded-lg p-4 bg-white">
              <div className="pb-4 flex-col justify-center items-start gap-1.5 inline-flex">
                <div className="text-2xl font-semibold font-['Inter'] leading-loose">
                  Reserva
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 pb-4">
                <div>
                  <p className="font-medium">{`${downsellDetailsData?.company?.name} | ID = ${downsellDetailsData?.company?.id}`}</p>
                  <p className="text-sm text-gray-500">Empresa</p>
                </div>
                <div>
                  <p className="font-medium">{`${downsellDetailsData?.cycleDetails?.id}`}</p>
                  <p className="text-sm text-gray-500">ID do ciclo do pacote</p>
                </div>
                <div>
                  <p className="font-medium">{`${downsellDetailsData?.booking.privateOfficeLabel}`}</p>
                  <p className="text-sm text-gray-500">Escritório privativo</p>
                </div>
                <div>
                  <p className="font-medium">{`${downsellDetailsData?.booking.privateOfficeId}`}</p>
                  <p className="text-sm text-gray-500">
                    ID do escritório privativo
                  </p>
                </div>
                <div>
                  <p className="font-medium">
                    {downsellDetailsData?.booking.credits.toFixed(2)}
                  </p>
                  <p className="text-sm text-gray-500">Créditos</p>
                </div>
                <div>
                  <p className="font-medium">{`${downsellDetailsData?.booking.take}%`}</p>
                  <p className="text-sm text-gray-500">porcentagem de take</p>
                </div>
                <div>
                  <p className="font-medium">{`${downsellDetailsData?.booking.members}`}</p>
                  <p className="text-sm text-gray-500">Quantidade de membros</p>
                </div>
              </div>
              <PackageDetails
                selectedPackage={{
                  ...downsellDetailsData.packageDetails,
                }}
                packageGroups={downsellDetailsData.packageDetails.groups}
              />
              <CycleDetails
                activeCycle={{
                  ...downsellDetailsData?.cycleDetails,
                  status: "",
                  anticipatedCreditsReceived: 0,
                  anticipatedCredits: 0,
                  credits: {
                    amount: downsellDetailsData?.cycleDetails?.credits,
                    courtesy: downsellDetailsData?.cycleDetails?.courtesy,
                    exceededPrice:
                      downsellDetailsData?.cycleDetails?.exceededPrice,
                    unitPrice: downsellDetailsData?.cycleDetails?.unitPrice,
                    consumption: {},
                  },
                }}
                showConsumption={false}
              />
            </div>
            <div className="flex-col justify-start items-start gap-1 self-stretch flex">
              <Alert className="bg-white rounded-lg border border-zinc-200 flex-col justify-start items-start gap-1 inline-flex p-4">
                <div className="pl-6">
                  <div className="absolute top-5 left-4">
                    <FileX2 width={16} height={16} />
                  </div>
                  <div>
                    <AlertTitle className="text-base font-medium text-bg-black mb-0">
                      Escolha de que forma esta reserva será cancelada.
                    </AlertTitle>
                    <AlertDescription className="self-stretch text-bg-black text-sm font-normal leading-tight">
                      Você pode cancelar apenas a reserva do mês atual, mantendo
                      as demais, ou todas as reservas futuras. Também é possível
                      editar os ciclos afetados pelo cancelamento.
                    </AlertDescription>
                    {!!errors.downsell_type?.message && (
                      <div className="text-red-500 text-sm font-normal leading-tight pt-1">
                        {errors.downsell_type.message}
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-x-4 justify-start place-items-baseline inline-flex mt-2">
                      <div className="justify-start items-center gap-2 inline-flex max-h-[25px]">
                        <Controller
                          control={control}
                          name="downsell_type"
                          render={({ field }) => (
                            <input
                              {...field}
                              type="radio"
                              id="booking-and-cycle"
                              value="booking-and-cycle"
                              className="h-4 w-4 peer disabled:cursor-not-allowed"
                              onChange={(e) => {
                                field.onChange(e);
                                handleBookingDownsellSimulation({
                                  companyId:
                                    downsellDetailsData?.company.id || 0,
                                  packageId:
                                    downsellDetailsData?.packageDetails.id || 0,
                                  bookingId: downsellDetailsData?.booking.id,
                                  cancelStrategy: e.target
                                    .value as DownsellType,
                                });
                              }}
                              checked={field.value === "booking-and-cycle"}
                              disabled={!isPackageMonthly}
                            />
                          )}
                        />
                        <label
                          htmlFor="booking-and-cycle"
                          className="text-bg-black text-sm font-medium peer-disabled:text-gray-400 peer-disabled:cursor-not-allowed"
                        >
                          Cancelar somente a reserva deste mês e editar ciclos.
                        </label>
                      </div>
                      <div className="justify-start items-center gap-2 inline-flex max-h-[25px]">
                        <Controller
                          control={control}
                          name="downsell_type"
                          render={({ field }) => (
                            <input
                              {...field}
                              type="radio"
                              value="booking-and-next-bookings-and-cycles"
                              id="booking-and-next-bookings-and-cycles"
                              className="h-4 w-4 peer disabled:cursor-not-allowed"
                              onChange={(e) => {
                                field.onChange(e);
                                handleBookingDownsellSimulation({
                                  companyId:
                                    downsellDetailsData?.company.id || 0,
                                  packageId:
                                    downsellDetailsData?.packageDetails.id || 0,
                                  bookingId: downsellDetailsData?.booking.id,
                                  cancelStrategy: e.target
                                    .value as DownsellType,
                                });
                              }}
                              checked={
                                field.value ===
                                "booking-and-next-bookings-and-cycles"
                              }
                              disabled={!isPackageMonthly}
                            />
                          )}
                        />
                        <label
                          htmlFor="booking-and-next-bookings-and-cycles"
                          className="text-black text-sm font-medium peer-disabled:text-gray-400 peer-disabled:cursor-not-allowed"
                        >
                          Cancelar todas as reservas futuras e editar ciclos.
                        </label>
                      </div>
                      <div className="justify-start items-center gap-2 inline-flex max-h-[25px]">
                        <Controller
                          control={control}
                          name="downsell_type"
                          render={({ field }) => (
                            <input
                              {...field}
                              type="radio"
                              value="booking-only"
                              id="booking-only"
                              className="h-4 w-4 peer disabled:cursor-not-allowed"
                              onChange={(e) => {
                                field.onChange(e);
                                handleBookingDownsellSimulation({
                                  companyId:
                                    downsellDetailsData?.company.id || 0,
                                  packageId:
                                    downsellDetailsData?.packageDetails.id || 0,
                                  bookingId: downsellDetailsData?.booking.id,
                                  cancelStrategy: e.target
                                    .value as DownsellType,
                                });
                              }}
                              checked={field.value === "booking-only"}
                            />
                          )}
                        />
                        <label
                          htmlFor="booking-only"
                          className="text-bg-black text-sm font-medium"
                        >
                          Cancelar somente a reserva deste mês e não editar
                          ciclos.
                        </label>
                      </div>
                      <div className="justify-start items-center gap-2 inline-flex max-h-[25px]">
                        <Controller
                          control={control}
                          name="downsell_type"
                          render={({ field }) => (
                            <input
                              {...field}
                              type="radio"
                              id="booking-and-next-bookings"
                              value="booking-and-next-bookings"
                              className="h-4 w-4"
                              onChange={(e) => {
                                field.onChange(e);
                                handleBookingDownsellSimulation({
                                  companyId:
                                    downsellDetailsData?.company.id || 0,
                                  packageId:
                                    downsellDetailsData?.packageDetails.id || 0,
                                  bookingId: downsellDetailsData?.booking.id,
                                  cancelStrategy: e.target
                                    .value as DownsellType,
                                });
                              }}
                              checked={
                                field.value === "booking-and-next-bookings"
                              }
                            />
                          )}
                        />
                        <label
                          htmlFor="booking-and-next-bookings"
                          className="text-bg-black text-sm font-medium"
                        >
                          Cancelar todas as reservas futuras e não editar
                          ciclos.
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </Alert>
            </div>

            {(watch("downsell_type") === "booking-and-cycle" ||
              watch("downsell_type") ===
                "booking-and-next-bookings-and-cycles") && (
              <>
                {hasPaidOrIsInvoicedInstallment ||
                  (!hasInstallmentWithEnoughCredits && (
                    <div className="flex-col justify-start items-start gap-1 self-stretch flex">
                      <Alert className="rounded-lg border border-red-500 w-full flex-col justify-start items-start gap-1 inline-flex">
                        <div className="flex gap-3  items-center pl-6">
                          <div className="absolute top-4 left-4">
                            <AlertCircle
                              className="text-red-500"
                              width={16}
                              height={16}
                            />
                          </div>
                          <div>
                            <AlertTitle className="text-base font-medium text-red-500 mb-0">
                              Alguns ciclos não podem sofrer edições!
                            </AlertTitle>
                            <AlertDescription className="self-stretch text-red-500 text-sm font-normal leading-tight">
                              Ciclos que já foram pagos ou faturados não serão
                              afetados e permanecerão inalterados..
                            </AlertDescription>
                          </div>
                        </div>
                      </Alert>
                    </div>
                  ))}
                <DownsellCyclesTable data={simulatedDownsell} />
              </>
            )}
            {(watch("downsell_type") === "booking-only" ||
              watch("downsell_type") === "booking-and-next-bookings") && (
              <AffectedBookingsTable data={simulatedDownsell} />
            )}
            <div className="self-stretch flex-col justify-start items-start gap-2.5 flex">
              <div className="self-stretch border border-zinc-200" />
            </div>
            <div className="mt-6 flex items-center justify-end gap-x-6">
              <Button
                className="bg-white text-black border border-zinc-200 hover:text-white transition-colors duration-200"
                onClick={() => navigate("/private-office/bookings")}
              >
                Voltar
              </Button>
              <Button type="submit" className="bg-red-500">
                Cancelar reserva
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
